import { useQueryClient } from '@tanstack/react-query';
import { LogOut, UserRoundCog } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { ImpersonationInfo } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';

const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Faixa fixa no topo do /app durante a impersonação. Publica a própria altura em `--imp-h`
 * para o menu lateral e o cabeçalho (sticky) se posicionarem abaixo dela em qualquer viewport.
 */
export function ImpersonationBanner({ info }: { info: ImpersonationInfo }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(() => new Date(info.expiresAt).getTime() - Date.now());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(info.expiresAt).getTime() - Date.now()), 1000);
    return () => clearInterval(t);
  }, [info.expiresAt]);

  useEffect(() => {
    if (left > 0) return;
    // expirou: a sessão não renova; volta ao login
    qc.clear();
    navigate('/entrar', { replace: true });
  }, [left, qc, navigate]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--imp-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--imp-h');
    };
  }, []);

  async function exit() {
    setBusy(true);
    try {
      const r = await api.post<{ organizationId: string }>('/platform/impersonate/exit');
      qc.clear();
      navigate(`/plataforma/organizacoes/${r.organizationId}`, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
      setBusy(false);
    }
  }

  const low = left < 5 * 60_000;
  return (
    <div
      ref={ref}
      role="status"
      className={`sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-3 py-2 text-sm font-medium ${low ? 'bg-red-600 text-white' : 'bg-amber-400 text-amber-950'}`}
    >
      <UserRoundCog className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 text-center">
        Você está acessando como {info.target.name} ({info.organizationName}) — <span className="tabular-nums">{mmss(left)}</span> restantes
      </span>
      <Button size="sm" variant="secondary" className="h-7 shrink-0" onClick={exit} disabled={busy}>
        <LogOut className="size-3.5" aria-hidden /> Sair
      </Button>
    </div>
  );
}
